/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * The live Android install banner, reused as the final step of the Mobile App
 * install guide. Mirrors the Android banners in pwaBanners.tsx: a Chromium
 * browser that captured `beforeinstallprompt` gets the one-tap "Install" banner;
 * an incompatible Android browser (Samsung Internet / Firefox) gets an
 * "open in Chrome" banner. Renders nothing when neither applies.
 */

import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import InfoBanner from '../misc/infoBanner';
import {onShowConfirmSnackbar} from '../../util/renderUtil';
import {
    hasInstallPrompt,
    isAndroid,
    isFirefox,
    isIOS,
    isMacSafari,
    isPWAInstalled,
    isSamsungBrowser,
    promptInstall,
    PWA_INSTALL_AVAILABLE_EVENT
} from '../../util/pwaUtil';

/** Which banner, if any, this browser can show right now. Exported so callers
 *  that frame the banner (the Mobile App guide numbers it as a step) can gate
 *  themselves on the SAME condition instead of a second guess — a step saying
 *  "follow the banner below" above nothing is worse than no step at all. */
export type AndroidInstallBannerMode = 'install' | 'open-in-chrome' | null;

export function getAndroidInstallBannerMode(): AndroidInstallBannerMode {
    if (hasInstallPrompt() && !isPWAInstalled() && !isIOS() && !isMacSafari() && !isFirefox()) return 'install';
    if (isAndroid() && !hasInstallPrompt() && (isSamsungBrowser() || isFirefox())) return 'open-in-chrome';
    return null;
}

/** Live version of {@link getAndroidInstallBannerMode}: re-evaluates whenever the
 *  install state changes (prompt captured, prompt spent, app installed). */
export function useAndroidInstallBannerMode(): AndroidInstallBannerMode {
    const [mode, setMode] = useState<AndroidInstallBannerMode>(getAndroidInstallBannerMode);

    useEffect(() => {
        const evaluate = () => setMode(getAndroidInstallBannerMode());
        evaluate();
        window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, evaluate);
        window.addEventListener('appinstalled', evaluate);
        return () => {
            window.removeEventListener(PWA_INSTALL_AVAILABLE_EVENT, evaluate);
            window.removeEventListener('appinstalled', evaluate);
        };
    }, []);

    return mode;
}

export default function AndroidInstallBanner() {
    const {t} = useTranslation();
    const mode = useAndroidInstallBannerMode();
    const [installLoading, setInstallLoading] = useState(false);

    function showError() {
        onShowConfirmSnackbar(t('pwa.actionError', 'Er ging iets mis. Probeer het opnieuw.'), {
            backgroundColor: 'color-red-warning',
            textColor: 'white'
        });
    }

    async function onClickInstall() {
        if (installLoading) return;
        setInstallLoading(true);
        try {
            const result = await promptInstall();
            if (result === 'unavailable') showError();
        } catch {
            showError();
        } finally {
            setInstallLoading(false);
        }
    }

    // Open the current URL in Chrome via an Android intent URL, with a Play
    // Store fallback if Chrome is absent. Same approach as pwaBanners.
    function openInChrome() {
        const baseUrl = window.location.href.split('#')[0];
        if (!baseUrl.startsWith('https://')) return;
        const hostAndPath = baseUrl.substring('https://'.length);
        const playStoreFallback = encodeURIComponent(
            'https://play.google.com/store/apps/details?id=com.android.chrome'
        );
        window.location.href =
            `intent://${hostAndPath}#Intent;scheme=https;` +
            'package=com.android.chrome;' +
            `S.browser_fallback_url=${playStoreFallback};end`;
    }

    // Chromium browser with a captured install prompt → one-tap install.
    if (mode === 'install')
        return (
            <InfoBanner
                variant='info'
                title={t('pwa.installBolmateTitle', 'Installeer Bolmate als app')}
                persistDismiss={false}
                primaryBannerButton
                actionLabel={t('pwa.install', 'Installeer')}
                onAction={onClickInstall}
                actionLoading={installLoading}
            >
                {t(
                    'pwa.installBolmate',
                    "Voeg Bolmate toe aan je bureaublad of startscherm. Als geïnstalleerde app start Bolmate sneller en kun je push-meldingen ontvangen over koopblok-wissels, lage voorraad en nieuwe reviews. Tik of klik op 'Installeer' om de installatiedialoog van je browser te openen."
                )}
            </InfoBanner>
        );

    // Incompatible Android browser (Samsung Internet / Firefox) → open in Chrome.
    if (mode === 'open-in-chrome')
        return (
            <InfoBanner
                variant='info'
                title={t('pwa.androidIncompatibleInstallTitle', 'Installeer Bolmate op je Android-toestel')}
                persistDismiss={false}
                primaryBannerButton
                actionLabel={t('pwa.openInChrome', 'In Chrome openen')}
                onAction={openInChrome}
            >
                {t(
                    'pwa.androidIncompatibleInstallHint',
                    'Je huidige browser ondersteunt geen één-klik installatie van Bolmate als app. Tik op de knop hieronder om Bolmate te openen in Google Chrome — daar kun je de app met één tik installeren. Heb je Chrome nog niet? Dan opent de Play Store om Chrome te installeren.'
                )}
            </InfoBanner>
        );

    return null;
}
